<template>
  <!-- Error State -->
  <aside 
    v-if="error"
    class="loader bg-primary-lighten-5" 
    :class="componentStyles"
  >
    <div class="loader__wrapper bg-white">
      <v-empty-state
        :image="emptyImage"
        :text="errorMessage || 'There might be a problem loading this page. Please check your internet connection or try again later.'"
        title="Page Load Error"
        color="primary"
      >
        <template #actions>
          <v-btn-primary
            color="primary"
            @click="retry"
          >
            Retry
          </v-btn-primary>
        </template>
      </v-empty-state>
    </div>
  </aside>

  <!-- Loading State -->
  <aside 
    v-else-if="showLoader"
    class="loader bg-primary-lighten-5" 
    :class="componentStyles"
  >
    <div class="loader__wrapper bg-white">
      <div class="load-animation">
        <span class="load-animation__top">
          <span class="load-animation__top--1" />
          <span class="load-animation__top--2" />
        </span>
        <span class="load-animation__bottom" />
      </div>
      Loading&hellip;
    </div>
  </aside>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import emptyImage from '@/../img/profile_done.svg'

export default defineComponent({
  name: 'RouterLoader',
  props: {
    error: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ''
    }
  },
  emits: ['retry'],
  data() {
    return {
      componentStyles: '',
      emptyImage,
      showLoader: false,
      showTimer: null as NodeJS.Timeout | null,
    }
  },
  watch: {
    error(newError) {
      if(newError) {
        // Clear timer and show error
        this.clearTimer()
        this.showLoader = true
        this.componentStyles = 'showLoader'
      }    
    }
  },
  mounted() {
    this.startLoadingTimer()
  },
  beforeUnmount() {
    this.clearTimer()
  },
  methods: {
    startLoadingTimer() {
      this.clearTimer()
      this.showTimer = setTimeout(() => {
        this.showLoader = true
        this.componentStyles = 'showLoader'
      }, 150)
    },
    clearTimer() {
      if(this.showTimer) {
        clearTimeout(this.showTimer)
        this.showTimer = null
      }
    },
    retry() {
      this.$emit('retry')
    }
  }
})
</script>

<style lang="scss" scoped>

.loader {
  bottom: 0;
  left: 0;
  padding: 5rem 1rem 1rem 1rem;
  position: fixed;
  right: 0;
  top: 0;
  opacity: 0;
  transform: translateY(3rem);
  transition: all 0.15s ease-in-out;
  z-index: 99;
  @media #{map-get(settings.$display-breakpoints, 'md-and-up')} {
    padding: 2rem 2rem 2rem 6rem;
    z-index: 100;
  }

  &.showLoader {
    opacity: 1;
    transform: translateY(0);
  }
}

.loader__wrapper {
  align-items: center;
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  padding: 2rem;
  width: 100%;
}

.load-animation {
  margin: 2rem auto 0.5rem;
  width: 6rem;

  > span {
    display: block;
    margin: 0 auto;
  }

  &__top {
    transition: all 0.3s ease-in-out;
    transform-origin: center bottom;
    width: 4rem;

    // this will swing back and forth, 20 degrees
    animation: swing 2s cubic-bezier(0.7,0.00,0.3,1.00) infinite;
    animation-direction: alternate;

    &--1 {
      background: url('@/../img/loader/idly-circle.png') no-repeat center center;
      background-size: contain;
      display: block;
      height: 1.5rem;
      left: 0.125rem;
      position: relative;
      width: 1.5rem;
      
      // this will roll back and forth, 270 degrees
      animation: roll 2s cubic-bezier(0.7,0,0.3,1.00) infinite;
      animation-direction: alternate;
    }

    &--2 {
      background: url('@/../img/loader/idly-rectangle.png') no-repeat center center;
      background-size: contain;
      display: block;
      height: 1.25rem;
      width: 4rem;
    }
  }

  &__bottom {
    background: url('@/../img/loader/idly-triangle.png') no-repeat center center;
    background-size: contain;
    height: 1.9375rem;
    width: 2.25rem;
  }
}

@keyframes swing {
  from {transform: rotate(15deg);}
  to {transform: rotate(-15deg);}
}

@keyframes roll {
  from {
    left: 0.125rem;
    transform: rotate(0deg);
  }
  to {
    left: 2.125rem;
    transform: rotate(270deg);
  }
}
</style>